import { FC, InputHTMLAttributes } from 'react';
import {
  cvaAltInput,
  cvaInput,
  cvaInputClear,
  cvaInputExtra,
} from '~/shared/ui/Input/InputStyles';
import clsx from 'clsx';
import Clear from 'public/icons/close.svg?react';
import { UseFormRegister } from 'react-hook-form';
import { cvaIcon } from '~/shared/styles/IconStyles';
import InputMask from 'react-input-mask';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  register?: UseFormRegister<any>;
  name: string;
  required?: boolean;
  error?: boolean;
  clearble?: boolean;
  clear?: () => void;
  altStyle?: boolean;
  textarea?: boolean;
  mask?: string;
  maskChar?: string;
}

export const Input: FC<Props> = ({
  register,
  name,
  textarea,
  required,
  className,
  error,
  clearble,
  clear,
  altStyle,
  mask,
  maskChar,
  ...rest
}) => {
  const currentStyle = altStyle ? cvaAltInput() : cvaInput({ error, textarea });

  return mask ? (
    <InputMask
      {...rest}
      id={name}
      onFocus={preventScrollWhenSoftKeyboardAppearsIfThereIsPlentyOfRoom}
      maskChar={maskChar ?? ' '}
      {...(register && register(name, { required: required }))}
      className={clsx(currentStyle, className)}
    />
  ) : textarea ? (
    <>
      <textarea
        autoComplete={'off'}
        maxLength={300}
        id={name}
        placeholder={{ ...rest }.placeholder}
        {...(register && register(name, { required: required }))}
        className={clsx(currentStyle, className)}
      />
      <div className={cvaInputExtra()}>
        {{ ...rest }?.value?.length ?? 0}/300
      </div>
    </>
  ) : (
    <>
      <div className={'relative'}>
        <input
          autoComplete={'off'}
          {...rest}
          id={name}
          {...(register && register(name, { required: required }))}
          className={clsx(currentStyle, className)}
        />
        {clearble && !{ ...rest }.disabled && (
          <Clear
            onClick={clear}
            className={clsx(cvaIcon({ iconSize: 'common' }), cvaInputClear())}
          />
        )}
      </div>
    </>
  );
};

const MAX_KEYBOARD_PROPORTION = 0.52;
const preventScrollWhenSoftKeyboardAppearsIfThereIsPlentyOfRoom = (e) => {
  // Bail if not iOS
  if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return;
  // Bail if there's too little vertical space and scrolling is necessary
  if (
    (e.target.offsetTop + e.target.offsetHeight) / window.innerHeight >
    MAX_KEYBOARD_PROPORTION
  )
    return;

  // Fix the position of the body momentarily to prevent user agent scrolling
  let offset = document.body.scrollTop;
  document.body.style.top = offset * -1 + 'px';
  document.body.classList.add('prevent-ios-focus-scrolling');
  setTimeout(() => {
    // Undo it after 500ms, roughly the amount of time focus animation takes iOS
    offset = parseInt(document.body.style.top, 10);
    document.body.classList.remove('prevent-ios-focus-scrolling');
    document.body.scrollTop = offset * -1;
  }, 500);
};
